import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Building2, Server, HardDrive, Calendar, Info, Loader } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../../utils/currencyUtils';
import type { ExchangeRates } from '../../utils/currencyUtils';

const applicationSchema = z.object({
  // Customer Information
  fullName: z.string().min(2, 'Full name is required'),
  companyName: z.string().optional(),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Valid phone number is required'),
  customerType: z.enum(['individual', 'business']),

  // Hosting Requirements
  machineCount: z.number().min(1, 'At least one machine is required'),
  hostingPackage: z.enum(['basic', 'professional', 'enterprise']),
  serverSpecs: z.object({
    cpu: z.string().min(1, 'CPU specification is required'),
    ram: z.string().min(1, 'RAM specification is required'),
    storage: z.string().min(1, 'Storage specification is required')
  }),
  operatingSystem: z.string().min(1, 'Operating system is required'),
  bandwidth: z.string().min(1, 'Bandwidth requirement is required'),
  contractDuration: z.number().min(1, 'Contract duration is required'),

  // Additional Details
  startDate: z.string().min(1, 'Start date is required'),
  specialRequirements: z.string().optional(),
  backupPreference: z.enum(['none', 'daily', 'weekly', 'monthly']),
  technicalContact: z.object({
    name: z.string().min(1, 'Technical contact name is required'),
    email: z.string().email('Invalid email address'),
    phone: z.string().min(10, 'Valid phone number is required')
  })
});

type ApplicationFormData = z.infer<typeof applicationSchema>;

interface HostingApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  facility: any;
  currency: string;
  exchangeRates: ExchangeRates;
}

const PACKAGE_PRICES = {
  basic: 150,
  professional: 300,
  enterprise: 500
};

const PACKAGE_FEATURES = {
  basic: [
    'Standard Support (9-5)',
    'Basic Monitoring',
    'Weekly Backups',
    'Shared Network'
  ],
  professional: [
    '24/7 Support',
    'Advanced Monitoring',
    'Daily Backups',
    'Dedicated Network',
    'Priority Maintenance'
  ],
  enterprise: [
    '24/7 Premium Support',
    'Real-time Monitoring',
    'Continuous Backups',
    'Dedicated Network',
    'Priority Maintenance',
    'Custom Solutions',
    'Dedicated Account Manager'
  ]
};

export function HostingApplicationModal({ 
  isOpen, 
  onClose, 
  facility,
  currency,
  exchangeRates
}: HostingApplicationModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [savedData, setSavedData] = useState<Partial<ApplicationFormData>>({});

  const { register, handleSubmit, control, watch, formState: { errors } } = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationSchema),
    defaultValues: savedData
  });

  const hostingPackage = useWatch({ control, name: 'hostingPackage' });
  const machineCount = useWatch({ control, name: 'machineCount' });

  const calculateEstimate = () => {
    const basePrice = PACKAGE_PRICES[hostingPackage as keyof typeof PACKAGE_PRICES] || 0;
    return basePrice * (machineCount || 1);
  };

  const onSubmit = async (data: ApplicationFormData) => {
    try {
      setIsSubmitting(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Form submitted:', data);
      toast.success('Application submitted successfully');
      onClose();
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveProgress = () => {
    const currentData = watch();
    setSavedData(currentData);
    toast.success('Progress saved');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name *</label>
                <input
                  type="text"
                  {...register('fullName')}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                />
                {errors.fullName && (
                  <p className="mt-1 text-sm text-red-600">{errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                <input
                  type="text"
                  {...register('companyName')}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email *</label>
                <input
                  type="email"
                  {...register('email')}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone *</label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Customer Type *</label>
                <div className="mt-2 space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      {...register('customerType')}
                      value="individual"
                      className="form-radio"
                    />
                    <span className="ml-2">Individual</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      {...register('customerType')}
                      value="business"
                      className="form-radio"
                    />
                    <span className="ml-2">Business</span>
                  </label>
                </div>
                {errors.customerType && (
                  <p className="mt-1 text-sm text-red-600">{errors.customerType.message}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Hosting Requirements</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Number of Machines *
                  <span className="ml-1 text-gray-500">
                    (Minimum: {facility.minOrder})
                  </span>
                </label>
                <input
                  type="number"
                  min={facility.minOrder}
                  {...register('machineCount', { valueAsNumber: true })}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                />
                {errors.machineCount && (
                  <p className="mt-1 text-sm text-red-600">{errors.machineCount.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Hosting Package *</label>
                <select
                  {...register('hostingPackage')}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                >
                  <option value="basic">Basic</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
                {errors.hostingPackage && (
                  <p className="mt-1 text-sm text-red-600">{errors.hostingPackage.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Package Features:</h4>
                  <ul className="space-y-1">
                    {PACKAGE_FEATURES[hostingPackage as keyof typeof PACKAGE_FEATURES]?.map((feature, index) => (
                      <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                        <span className="w-1 h-1 bg-blue-500 rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="md:col-span-2">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Server Specifications *</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700">CPU</label>
                    <input
                      type="text"
                      {...register('serverSpecs.cpu')}
                      className="mt-1 block w-full rounded-lg border-gray-300"
                      placeholder="e.g., 8 cores"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">RAM</label>
                    <input
                      type="text"
                      {...register('serverSpecs.ram')}
                      className="mt-1 block w-full rounded-lg border-gray-300"
                      placeholder="e.g., 32GB"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Storage</label>
                    <input
                      type="text"
                      {...register('serverSpecs.storage')}
                      className="mt-1 block w-full rounded-lg border-gray-300"
                      placeholder="e.g., 1TB SSD"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Operating System *</label>
                <select
                  {...register('operatingSystem')}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                >
                  <option value="ubuntu">Ubuntu Server</option>
                  <option value="centos">CentOS</option>
                  <option value="windows">Windows Server</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Bandwidth *</label>
                <select
                  {...register('bandwidth')}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                >
                  <option value="1gbps">1 Gbps</option>
                  <option value="10gbps">10 Gbps</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Contract Duration (months) *</label>
                <select
                  {...register('contractDuration', { valueAsNumber: true })}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                >
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                  <option value={24}>24 months</option>
                  <option value={36}>36 months</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Additional Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date *</label>
                <input
                  type="date"
                  {...register('startDate')}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Backup Preference *</label>
                <select
                  {...register('backupPreference')}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                >
                  <option value="none">No Backup</option>
                  <option value="daily">Daily Backup</option>
                  <option value="weekly">Weekly Backup</option>
                  <option value="monthly">Monthly Backup</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Special Requirements</label>
                <textarea
                  {...register('specialRequirements')}
                  rows={4}
                  className="mt-1 block w-full rounded-lg border-gray-300"
                  placeholder="Any special configurations or requirements..."
                />
              </div>

              <div className="md:col-span-2">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Technical Contact *</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700">Name</label>
                    <input
                      type="text"
                      {...register('technicalContact.name')}
                      className="mt-1 block w-full rounded-lg border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Email</label>
                    <input
                      type="email"
                      {...register('technicalContact.email')}
                      className="mt-1 block w-full rounded-lg border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700">Phone</label>
                    <input
                      type="tel"
                      {...register('technicalContact.phone')}
                      className="mt-1 block w-full rounded-lg border-gray-300"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-blue-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Estimated Monthly Cost</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Package ({hostingPackage})</span>
                  <span className="font-medium">
                    {formatCurrency(PACKAGE_PRICES[hostingPackage as keyof typeof PACKAGE_PRICES], currency, exchangeRates)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Number of Machines</span>
                  <span className="font-medium">x{machineCount || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Power Cost (est.)</span>
                  <span className="font-medium">
                    {formatCurrency(facility.powerCost * 720 * (machineCount || 0), currency, exchangeRates)}
                  </span>
                </div>
                <div className="border-t border-blue-100 pt-2 mt-2">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total Estimated Cost</span>
                    <span>
                      {formatCurrency(
                        calculateEstimate() + (facility.powerCost * 720 * (machineCount || 0)),
                        currency,
                        exchangeRates
                      )}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    * Actual costs may vary based on power usage and additional services
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
              onClick={onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Building2 className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        Hosting Application
                      </h2>
                      <p className="text-sm text-gray-500">
                        {facility.name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Progress Steps */}
                <div className="flex items-center justify-between mt-6">
                  {[1, 2, 3].map((step) => (
                    <button
                      key={step}
                      onClick={() => setCurrentStep(step)}
                      className={`flex-1 relative ${
                        step < currentStep ? 'text-blue-600' :
                        step === currentStep ? 'text-gray-900' :
                        'text-gray-400'
                      }`}
                    >
                      <div className="h-1 absolute left-0 right-0 top-4 -translate-y-1/2">
                        <div className={`h-full transition-all ${
                          step <= currentStep ? 'bg-blue-600' : 'bg-gray-200'
                        }`} />
                      </div>
                      <div className="relative flex items-center justify-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                          step < currentStep ? 'bg-blue-600 text-white' :
                          step === currentStep ? 'bg-white border-2 border-blue-600' :
                          'bg-gray-100 text-gray-400'
                        }`}>
                          {step}
                        </div>
                      </div>
                      <span className="mt-2 block text-xs">
                        {step === 1 ? 'Customer Info' :
                         step === 2 ? 'Requirements' :
                         'Details'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="px-6 py-4 max-h-[calc(100vh-250px)] overflow-y-auto">
                  {renderStepContent()}
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between">
                  <button
                    type="button"
                    onClick={saveProgress}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Save Progress
                  </button>
                  
                  <div className="flex gap-3">
                    {currentStep > 1 && (
                      <button
                        type="button"
                        onClick={() => setCurrentStep(prev => prev - 1)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Previous
                      </button>
                    )}
                    
                    {currentStep < 3 ? (
                      <button
                        type="button"
                        onClick={() => setCurrentStep(prev => prev + 1)}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Application'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}